import { io } from "socket.io-client";

class SupportChatService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.sessionId = null;
    this.userType = null;
    this.messageQueue = [];
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.callbacks = {
      onConnect: null,
      onDisconnect: null,
      onMessage: null,
      onTyping: null,
      onSessionStarted: null,
      onSessionEnded: null,
      onOnlineUsers: null,
      onUserOnline: null,
      onUserOffline: null,
      onNewChatSession: null,
      onSessionUpdated: null,
      onActiveSessions: null,
      onSessionJoined: null,
      onNoAdminsAvailable: null,
      onExistingSessionFound: null,
      onNoExistingSession: null,
      onAdminStatus: null,
      onError: null,
      onNewMessage: null,
    };
  }

  // Connect to chat server
  connect(userData) {
    
    if (this.socket) {
      this.disconnect();
    }

    // Sačuvaj tip korisnika
    this.userType = userData.userType;

    const serverUrl = process.env.REACT_APP_WEBSOCKET_URL || "https://mrc.potentium.rs";
    
    this.socket = io(serverUrl, {
      transports: ["websocket", "polling"],
      auth: {
        userId: userData.id,
        userType: userData.userType,
        username: userData.username,
        email: userData.email,
      },
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    this.setupEventListeners();

    this.socket.on("connect", () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      console.log(`${userData.userType} connected to chat server`);
      
      // Process queued messages
      this.processMessageQueue();

      if (this.callbacks.onConnect) {
        this.callbacks.onConnect();
      }
    });

    this.socket.on("connect_error", (error) => {
      console.error(`${userData.userType} connection error:`, error);
    });

    this.socket.on("disconnect", (reason) => {
      console.log(`${userData.userType} disconnected:`, reason);
    });

    this.socket.on("disconnect", () => {
      this.isConnected = false;
      console.log(`${userData.userType} disconnected from chat server`);

      if (this.callbacks.onDisconnect) {
        this.callbacks.onDisconnect();
      }
    });
  }

  setupEventListeners() {
    // New messages
    this.socket.on("newMessage", (data) => {
      console.log("New message received:", data);
      if (this.callbacks.onMessage) {
        this.callbacks.onMessage(data);
      }
    });

    // Typing indicators - POBOLJŠANO
    this.socket.on("userTyping", (data) => {
      console.log("Typing indicator received:", data);
      if (this.callbacks.onTyping) {
        this.callbacks.onTyping(data);
      }
    });

    // Session events
    this.socket.on("sessionStarted", (data) => {
      this.sessionId = data.sessionId;
      if (this.callbacks.onSessionStarted) {
        this.callbacks.onSessionStarted(data);
      }
    });

    this.socket.on("sessionEnded", (data) => {
      console.log("Session ended:", data);
      if (data.sessionId === this.sessionId) {
        this.sessionId = null;
      }
      if (this.callbacks.onSessionEnded) {
        this.callbacks.onSessionEnded(data);
      }
    });

    this.socket.on("sessionJoined", (data) => {
      console.log("Session joined:", data);
      if (this.callbacks.onSessionJoined) {
        this.callbacks.onSessionJoined(data);
      }
    });

    // Online users
    this.socket.on("onlineUsers", (data) => {
      if (this.callbacks.onOnlineUsers) {
        this.callbacks.onOnlineUsers(data);
      }
    });

    this.socket.on("userOnline", (data) => {
      if (this.callbacks.onUserOnline) {
        this.callbacks.onUserOnline(data);
      }
    });

    this.socket.on("userOffline", (data) => {
      if (this.callbacks.onUserOffline) {
        this.callbacks.onUserOffline(data);
      }
    });

    // Admin specific events
    this.socket.on("activeSessions", (data) => {
      console.log("Active sessions received (activeSessions):", data);
      if (this.callbacks.onActiveSessions) {
        this.callbacks.onActiveSessions(data);
      }
    });

    // Also listen for active_sessions (with underscore) for compatibility
    this.socket.on("active_sessions", (data) => {
      console.log("Active sessions received (active_sessions):", data);
      if (this.callbacks.onActiveSessions) {
        this.callbacks.onActiveSessions(data.sessions || data);
      }
    });

    this.socket.on("newChatSession", (data) => {
      console.log("New chat session received:", data);
      if (this.callbacks.onNewChatSession) {
        this.callbacks.onNewChatSession(data);
      }
    });

    this.socket.on("sessionUpdated", (data) => {
      console.log("Session updated:", data);
      if (this.callbacks.onSessionUpdated) {
        this.callbacks.onSessionUpdated(data);
      }
    });

    // Email fallback
    this.socket.on("noAdminsAvailable", (data) => {
      console.log("No admins available:", data);
      if (this.callbacks.onNoAdminsAvailable) {
        this.callbacks.onNoAdminsAvailable(data);
      }
    });

    // Existing session events
    this.socket.on("existingSessionFound", (data) => {
      console.log("Existing session found:", data);
      this.sessionId = data.sessionId;
      if (this.callbacks.onExistingSessionFound) {
        this.callbacks.onExistingSessionFound(data);
      }
    });

    this.socket.on("noExistingSession", () => {
      console.log("No existing session");
      if (this.callbacks.onNoExistingSession) {
        this.callbacks.onNoExistingSession();
      }
    });

    // Admin status events - POBOLJŠANO
    this.socket.on("adminStatus", (data) => {
      console.log("Admin status received:", data);
      if (this.callbacks.onAdminStatus) {
        this.callbacks.onAdminStatus(data);
      }
    });

    // Dodano: adminStatusUpdate event
    this.socket.on("adminStatusUpdate", (data) => {
      console.log("Admin status update received:", data);
      if (this.callbacks.onAdminStatus) {
        this.callbacks.onAdminStatus(data);
      }
    });

    // User online/offline events
    this.socket.on("user_online", (data) => {
      console.log("User came online:", data);
      if (this.callbacks.onUserOnline) {
        this.callbacks.onUserOnline(data);
      }
    });

    this.socket.on("user_offline", (data) => {
      console.log("User went offline:", data);
      if (this.callbacks.onUserOffline) {
        this.callbacks.onUserOffline(data);
      }
    });

    // Error handling
    this.socket.on("error", (data) => {
      console.error("Socket error:", data);
      if (this.callbacks.onError) {
        this.callbacks.onError(data);
      }
    });

    // Listen for new_message events (sent to admin room when band sends message)
    this.socket.on("new_message", (data) => {
      console.log("New message notification received:", data);
      if (this.callbacks.onNewMessage) {
        this.callbacks.onNewMessage(data);
      }
    });
  }

  // Set callbacks
  setCallbacks(callbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  // Check if connected
  isSocketConnected() {
    return this.socket && this.isConnected;
  }

  // DODANO: Request admin status
  requestAdminStatus() {
    if (this.socket && this.isConnected) {
      console.log("Requesting admin status");
      this.socket.emit("requestAdminStatus");
    }
  }

  // Check for existing session (for band users)
  checkExistingSession() {
    if (this.socket && this.isConnected) {
      console.log("Checking for existing session");
      this.socket.emit("checkExistingSession");
    }
  }

  // Start chat session (for band users)
  startChatSession(initialMessage) {
    if (this.socket && this.isConnected) {
      console.log("Starting chat session with message:", initialMessage);
      this.socket.emit("startChatSession", {
        initialMessage,
      });
    }
  }

  // Join session (for admins)
  joinSession(sessionId) {
    if (this.socket && this.isConnected) {
      console.log("Joining session:", sessionId);
      this.sessionId = sessionId; // Postavi sessionId kada se joinuje
      this.socket.emit("joinSession", {
        sessionId,
      });
    }
  }

  // Send message
  sendMessage(message) {
    const messageData = {
      sessionId: this.sessionId,
      message: message.trim(),
      timestamp: new Date().toISOString(),
    };

    if (this.socket && this.isConnected && this.sessionId) {
      console.log("Sending message:", message, "to session:", this.sessionId);
      this.socket.emit("sendMessage", messageData);
    } else {
      // Queue message for later sending
      console.warn("Message queued - not connected or no session ID");
      this.messageQueue.push(messageData);
    }
  }

  // Process queued messages
  processMessageQueue() {
    while (this.messageQueue.length > 0 && this.sessionId) {
      const message = this.messageQueue.shift();
      this.socket.emit("sendMessage", message);
      console.log("Sent queued message:", message.message);
    }
  }

  // Send typing indicator - POBOLJŠANO
  sendTyping(isTyping) {
    if (this.socket && this.isConnected && this.sessionId) {
      this.socket.emit("typing", {
        sessionId: this.sessionId,
        isTyping: isTyping,
        userType: this.userType, // Dodano userType
      });
    } else {
      console.warn("Cannot send typing - not connected or no session ID");
    }
  }

  // End session (for admins)
  endSession(sessionId) {
    if (this.socket && this.isConnected) {
      const targetSessionId = sessionId || this.sessionId;
      console.log("Ending session:", targetSessionId);
      this.socket.emit("endSession", {
        sessionId: targetSessionId,
      });

      // Ako završavamo trenutnu sesiju, resetuj sessionId
      if (targetSessionId === this.sessionId) {
        this.sessionId = null;
      }
    }
  }

  // Get connection status
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      sessionId: this.sessionId,
      userType: this.userType,
    };
  }

  // Set session ID (for admins joining specific sessions)
  setSessionId(sessionId) {
    console.log("Setting session ID:", sessionId);
    this.sessionId = sessionId;
  }

  // Disconnect
  disconnect() {
    if (this.socket) {
      console.log("Disconnecting from chat server");
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.sessionId = null;
      this.userType = null;
      this.messageQueue = [];
      this.reconnectAttempts = 0;
    }
  }
}

// Export singleton instance
export default new SupportChatService();
